import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Dropdown,
  Button,
  InputGroup,
  Row,
  Col,
  Spinner,
  Table,
  Pagination,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import PageHeader from "../shared/PageHeader";
import ImagePreviewModal from "../shared/ImagePreviewModal";
import { Order } from "../../types";
import { getOrders, deleteOrder } from "../../services/OrderService";
import { SupplierInfo } from "../../types/supplier";
import { getSuppliersDropdown } from "../../services/SupplierService";
import { useOrdersTableRefresh } from "../../hooks/useOrdersTableRefresh";
import { schemaMap } from "../../formSchemas";

const TEMPLATE_ROUTE_OVERRIDES: Record<string, string> = {
  "daily cake order": "daily-order-cakes",
};

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const templateToRoute = (template: string): string => {
  const key = template.toLowerCase().trim();
  const override = TEMPLATE_ROUTE_OVERRIDES[key];
  if (override) return override;

  const slug = slugify(template);
  if (schemaMap[slug]) return slug;

  return "cake-accessories";
};

const parseTemplates = (raw?: string | null): string[] =>
  (raw || "Standard").split(",").map((s) => s.trim()).filter(Boolean);

const formatDate = (value?: string): string => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value?: string): string => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }) + ", " + date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusClasses = (status: string): string => {
  const normalized = status.toLowerCase();

  if (normalized.includes("complete")) {
    return "bg-success-subtle text-success-emphasis border-success-subtle";
  }

  if (normalized.includes("progress")) {
    return "bg-warning-subtle text-warning-emphasis border-warning-subtle";
  }

  if (normalized.includes("draft")) {
    return "bg-secondary-subtle text-secondary-emphasis border-secondary-subtle";
  }

  return "bg-light text-body-secondary border-light";
};

const canEditOrDelete = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return normalized === "draft" || normalized === "pending";
};

const getFirstImage = (order: Order): string | null => {
  if (!order.formData) return null;
  try {
    const parsed = JSON.parse(order.formData);
    for (const key of ["picture1", "picture2", "image"]) {
      if (parsed[key] && typeof parsed[key] === "string" && parsed[key].startsWith("data:image")) {
        return parsed[key];
      }
    }
  } catch {}
  return null;
};

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedSupplier, setSelectedSupplier] = useState<string>("all");
  const [dateOrdered, setDateOrdered] = useState("");
  const [requiredBy, setRequiredBy] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [entriesPerPage, setEntriesPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const transformOrders = useCallback((result: any[]): Order[] => {
    return result.map((order: any) => ({
      id: order.id,
      image: order.image || "",
      orderNumber: order.orderNumber,
      clientId: order.clientId,
      type: order.type,
      dateOrdered:
        typeof order.dateOrdered === "string"
          ? order.dateOrdered
          : new Date(order.dateOrdered).toISOString(),
      dateRequired:
        typeof order.dateRequired === "string"
          ? order.dateRequired
          : new Date(order.dateRequired).toISOString().split("T")[0],
      total: order.total || 0,
      status: order.status,
      isEdited: order.isEdited,
      formData: order.formData,
      supplierId: order.supplierId,
    }));
  }, []);

  const fetchOrders = useCallback(async () => {
    const result = await getOrders({
      type: selectedType,
      dateOrdered: dateOrdered || undefined,
      dateRequired: requiredBy || undefined,
      supplierId: selectedSupplier !== "all" ? Number(selectedSupplier) : undefined,
    });

    return result ? transformOrders(result) : [];
  }, [dateOrdered, requiredBy, selectedType, selectedSupplier, transformOrders]);

  const { refreshOrders, isRefreshing } = useOrdersTableRefresh<Order>(
    fetchOrders,
    setOrders,
    setLoading,
    (error) => console.error("Failed to fetch orders:", error)
  );

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const result = await getSuppliersDropdown();
        if (result && Array.isArray(result)) {
          setSuppliers(result);
        }
      } catch (error) {
        console.error("Failed to fetch suppliers:", error);
      }
    };

    fetchSuppliers();
  }, []);

  useEffect(() => {
    refreshOrders({ showLoading: true });
  }, [refreshOrders]);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage, selectedType, selectedSupplier, dateOrdered, requiredBy]);

  const getTemplatesForSupplier = (supplier: SupplierInfo): string[] =>
    parseTemplates(supplier.templateSelection);

  const filterOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    suppliers.forEach((supplier) => {
      parseTemplates(supplier.templateSelection).forEach((template) => {
        const value = templateToRoute(template);
        if (!seen.has(value)) {
          seen.add(value);
          options.push({ value, label: template });
        }
      });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [suppliers]);

  const handleNewOrder = (supplier: SupplierInfo, template: string) => {
    const route = templateToRoute(template);
    navigate(`/orders/new/${route}`, { state: { supplierId: supplier.id } });
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    try {
      const result = await deleteOrder(orderId);
      if (result) {
        refreshOrders();
      }
    } catch (error) {
      console.error("Failed to delete order:", error);
    }
  };

  const filteredOrders = orders.filter((order) => {
    return (
      !searchTerm ||
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.clientId?.toString().includes(searchTerm) ||
      order.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / entriesPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * entriesPerPage;
  const displayedOrders = filteredOrders.slice(startIndex, startIndex + entriesPerPage);
  const startEntry = filteredOrders.length === 0 ? 0 : startIndex + 1;
  const endEntry =
    filteredOrders.length === 0
      ? 0
      : Math.min(startIndex + displayedOrders.length, filteredOrders.length);

  return (
    <div className="container-fluid min-vh-100 bg-body-tertiary px-3 px-lg-4 py-4">
      <div className="d-flex flex-column gap-3">
        <PageHeader title="Orders" subTitle="Orders" />

        <div className="d-flex flex-column flex-sm-row gap-2 justify-content-between align-items-stretch align-items-sm-center">
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              id="new-order-dropdown"
              className="px-4 shadow-sm"
            >
              <i className="bi bi-plus-lg me-2"></i>
              New Order
            </Dropdown.Toggle>
            <Dropdown.Menu className="dropdown-menu-responsive">
              {suppliers.length > 0 ? (
                suppliers.map((supplier) => {
                  const templates = getTemplatesForSupplier(supplier);
                  if (templates.length <= 1) {
                    return (
                      <Dropdown.Item
                        key={supplier.id}
                        as="button"
                        onClick={() => handleNewOrder(supplier, templates[0] || "Default")}
                      >
                        {supplier.businessDisplayName}
                      </Dropdown.Item>
                    );
                  }
                  return (
                    <div key={supplier.id} className="dropdown-submenu">
                      <button
                        type="button"
                        className="dropdown-item dropdown-toggle"
                      >
                        {supplier.businessDisplayName}
                      </button>
                      <ul className="dropdown-menu">
                        {templates.map((template) => (
                          <li key={template}>
                            <Dropdown.Item
                              as="button"
                              onClick={() => handleNewOrder(supplier, template)}
                            >
                              {template}
                            </Dropdown.Item>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })
              ) : (
                <Dropdown.Item disabled>Loading suppliers...</Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="light"
            onClick={() => refreshOrders()}
            disabled={isRefreshing}
            className="border rounded-pill px-4 shadow-sm d-inline-flex align-items-center gap-2"
          >
            {isRefreshing ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Refreshing...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </>
            )}
          </Button>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-lg-4">
            <Row className="g-3 mb-3">
              <Col xs={12} md={6} lg={3}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-grid"></i>
                  </InputGroup.Text>
                  <Form.Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="border-start-0"
                    aria-label="Order type"
                  >
                    <option value="all">All order types</option>
                    {filterOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-shop"></i>
                  </InputGroup.Text>
                  <Form.Select
                    value={selectedSupplier}
                    onChange={(e) => setSelectedSupplier(e.target.value)}
                    className="border-start-0"
                    aria-label="Supplier"
                  >
                    <option value="all">All suppliers</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.businessDisplayName}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar-event"></i>
                  </InputGroup.Text>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-date-ordered">Date Ordered</Tooltip>
                    }
                  >
                    <Form.Control
                      type="date"
                      value={dateOrdered}
                      onChange={(e) => setDateOrdered(e.target.value)}
                      className="border-start-0"
                      aria-label="Date ordered"
                    />
                  </OverlayTrigger>
                </InputGroup>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-end-0">
                    <i className="bi bi-calendar2-check"></i>
                  </InputGroup.Text>
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-date-required">Date Required</Tooltip>
                    }
                  >
                    <Form.Control
                      type="date"
                      value={requiredBy}
                      onChange={(e) => setRequiredBy(e.target.value)}
                      className="border-start-0"
                      aria-label="Date required"
                    />
                  </OverlayTrigger>
                </InputGroup>
              </Col>
            </Row>

            <Row className="g-3 align-items-center mb-3">
              <Col xs={12} md="auto">
                <div className="d-flex align-items-center gap-2 text-body-secondary small">
                  <span>Show</span>
                  <Form.Select
                    size="sm"
                    value={entriesPerPage}
                    onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                    className="w-auto"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  <span>entries</span>
                </div>
              </Col>
              <Col xs={12} md={4} className="ms-md-auto">
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search orders..."
                  />
                </InputGroup>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="small text-uppercase text-secondary">
                  <tr>
                    <th className="border-bottom pb-3 pt-2">Order</th>
                    <th className="border-bottom pb-3 pt-2">Image</th>
                    <th className="border-bottom pb-3 pt-2">Customer</th>
                    <th className="border-bottom pb-3 pt-2">Date Ordered</th>
                    <th className="border-bottom pb-3 pt-2">Date Required</th>
                    <th className="border-bottom pb-3 pt-2">Total</th>
                    <th className="border-bottom pb-3 pt-2">Status</th>
                    <th className="border-bottom pb-3 pt-2 text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-5">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading orders...
                      </td>
                    </tr>
                  ) : displayedOrders.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-5">
                        No orders found.
                      </td>
                    </tr>
                  ) : (
                    displayedOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="py-4 fw-semibold text-dark">
                          {order.orderNumber || "-"}
                        </td>
                        <td className="py-4">
                          {getFirstImage(order) ? (
                            <a
                              href="#"
                              className="text-primary text-decoration-none small"
                              onClick={(e) => {
                                e.preventDefault();
                                setPreviewImage(getFirstImage(order));
                              }}
                            >
                              <i className="bi bi-image me-1"></i>View
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="py-4">{order.clientId || "-"}</td>
                        <td className="py-4 text-body-secondary">
                          {formatDateTime(order.dateOrdered)}
                        </td>
                        <td className="py-4 text-body-secondary">
                          {formatDate(order.dateRequired)}
                        </td>
                        <td className="py-4">£ {order.total || 0}</td>
                        <td className="py-4">
                          <span
                            className={`badge rounded-pill border px-3 py-2 fw-semibold ${getStatusClasses(
                              order.status || ""
                            )}`}
                          >
                            {order.status || "Pending"}
                          </span>
                          {order.isEdited && (
                            <span
                              className="badge rounded-pill bg-info-subtle text-info-emphasis border-info-subtle border px-2 py-1 ms-1"
                              title="This order has been edited"
                            >
                              <i className="bi bi-pencil-fill me-1"></i>
                              Edited
                            </span>
                          )}
                        </td>
                        <td className="py-4 text-end">
                          <div className="d-flex gap-1 justify-content-end">
                            <Button
                              variant="light"
                              size="sm"
                              onClick={() => navigate(`/orders/view/${order.id}`)}
                              className="border rounded-pill px-3 shadow-sm"
                            >
                              <i className="bi bi-eye me-1"></i>
                              View
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => navigate(`/orders/edit/${order.id}`)}
                              disabled={!canEditOrDelete(order.status || "")}
                              className="border rounded-pill px-3 shadow-sm"
                              title={
                                canEditOrDelete(order.status || "")
                                  ? "Edit this order"
                                  : "Only Draft or Pending orders can be edited"
                              }
                            >
                              <i className="bi bi-pencil me-1"></i>
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteOrder(order.id!)}
                              disabled={!canEditOrDelete(order.status || "")}
                              className="border rounded-pill px-3 shadow-sm"
                              title={
                                canEditOrDelete(order.status || "")
                                  ? "Delete this order"
                                  : "Only Draft or Pending orders can be deleted"
                              }
                            >
                              <i className="bi bi-trash me-1"></i>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border-top mt-3 pt-3">
              <div className="small text-muted">
                Showing rows {startEntry} to {endEntry} of {filteredOrders.length}
              </div>

              <Pagination size="sm" className="mb-0 ms-md-auto">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === safeCurrentPage}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
              </Pagination>
            </div>
          </Card.Body>
        </Card>
      </div>

      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />
    </div>
  );
};

export default Orders;
